<script lang="ts" setup>
  import CustomModal from '@/components/common/CustomModal.vue'
  import LinkGenerationModal from '@/components/common/LinkGenerationModal.vue'
  import { useEmitter } from '@/composable/useEmitter'
  import { useDocument, useNotify } from '@/composition'
  import { useAppStore } from '@/store/app'
  import { useBuilderStore } from '@/store/builder'
  import { useProductListStore } from '@/store/productListStore'
  import {
    ELEMENTS_LOOKUP,
    RecipientEntityTypeEnum,
    TemplateTypeEnum,
    TemplateValidationModal,
    flatChildrens,
  } from '@gohighlevel/ghl-proposals-common'
  import { DefaultValidationObjectForTemplate } from '@gohighlevel/ghl-proposals-common/src/const/productLineItems'
  import { UIButton, UITooltip } from '@gohighlevel/ghl-ui'
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  const productListStore = useProductListStore()
  const showActionConfirmationModal = ref<boolean>(false)
  const activeAction = ref<string | null>(null)
  const actionModalTitle = ref('')
  const actionModalDescription = ref('')
  const validations = ref(DefaultValidationObjectForTemplate)
  const isAllValid = ref(false)
  const appStore = useAppStore()
  const builderStore = useBuilderStore()
  const showErrorModal = ref(false)
  const scheduleErrors = computed(() => productListStore.getProductListErrors())
  const settingsErrors = ref(appStore.getSettingsErrors())
  const redirectUrlErrors = computed(() => appStore.getRedirectUrlSettingErrors())
  const customValueLinkageErrors = computed(() => builderStore.getCustomValueLinkageErrors())

  watch(
    () => appStore.document.notificationSettings,
    () => {
      settingsErrors.value = appStore.getSettingsErrors()
    },
    { deep: true }
  )
  const { loading, saveDocument, convertToProposal, removeDocument, pushToListPage, validateDocument } = useDocument()
  const { handleErrorMessage } = useNotify()
  const { t } = useI18n()
  const emitter = useEmitter()

  // Custom roles are a template-only concept. A document with any field assigned to a role
  // cannot be published as a public document or saved to the content library.
  const hasRoleAssignedElement = computed(() => {
    const allElements = flatChildrens(builderStore.pages || [])
    return allElements.some((el: any) => el?.component?.options?.entityName === RecipientEntityTypeEnum.ROLE)
  })

  const closeActionModal = () => {
    showActionConfirmationModal.value = false
    activeAction.value = null
    actionModalTitle.value = ''
    actionModalDescription.value = ''
  }

  const toggleModal = () => {
    showErrorModal.value = !showErrorModal.value
  }

  const onSuccessFullAction = async () => {
    if (activeAction.value === 'delete') {
      await removeDocument()
      pushToListPage()
    }
    closeActionModal()
  }

  const useAsTemplateAction = async () => {
    validations.value = validateDocument()
    isAllValid.value = Object.values(validations.value).every(val => val === true)
    if (!isAllValid.value) {
      toggleModal()
    } else {
      await saveDocument()
      await convertToProposal()
    }
  }

  const onClickSaveAction = async () => {
    appStore.sendAttempted = true
    // Content-library saves are allowed for role templates. The recipient selector is disabled
    // for content-library templates (assignments are sanitized when the content is used), so
    // there is nothing to block here — only public documents remain restricted (see publishDocForm).
    validations.value = validateDocument()
    isAllValid.value = Object.values(validations.value).every(val => val === true)
    if (!isAllValid.value) {
      toggleModal()
    } else {
      await saveDocument()
    }
  }
  const isPublished = computed(() => appStore.document.isPublished)
  const publishDocForm = async () => {
    appStore.sendAttempted = true
    // Public documents cannot contain role-assigned fields.
    if (hasRoleAssignedElement.value) {
      handleErrorMessage(t('common.rolesNotAllowedInPublic'))
      return
    }
    validations.value = validateDocument(isPublished.value ? { skipSaveCardValidations: true } : undefined)
    isAllValid.value = Object.values(validations.value).every(v => v === true)
    if (!isAllValid.value) {
      // open the existing validation modal in this component
      showErrorModal.value = true
      return
    }
    emitter.emit('open-link-generation-model')
  }

  const isContentLibraryTemplate = computed(() => {
    return appStore.document.type === TemplateTypeEnum.CONTENT_LIBRARY
  })

  const hasSaveCardDetailsElement = computed(() => {
    const allElements = flatChildrens(builderStore.pages || [])
    return allElements.some((el: { type?: string }) => el.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS)
  })
</script>

<template>
  <TemplateValidationModal
    :show="showErrorModal"
    @on-cancel="toggleModal"
    :isAllValid="isAllValid"
    :product-list-errors="scheduleErrors"
    :validations="validations"
    :notificationSettings="appStore.document.notificationSettings"
    :settingsErrors="settingsErrors"
    :redirectUrlErrors="redirectUrlErrors"
    :showOnlyProductListErrors="isContentLibraryTemplate"
    :customValueLinkageErrors="customValueLinkageErrors"
    :hasSaveCardDetailsElement="hasSaveCardDetailsElement"
  />
  <CustomModal
    :loading="loading"
    :show="showActionConfirmationModal"
    modal-type="error"
    success-action-text="Yes"
    cancel-action-text="No"
    :title="actionModalTitle"
    @on-cancel="closeActionModal"
    @on-success="onSuccessFullAction"
  >
    {{ actionModalDescription }}
  </CustomModal>
  <LinkGenerationModal />
  <div class="flex gap-4">
    <div v-if="!isContentLibraryTemplate">
      <UITooltip>
        {{ !isPublished ? $t('templates.createDocForm') : $t('templates.unpublishDocForm') }}
        <template #trigger>
          <UIButton id="publishDocForm" ghost @click="publishDocForm" type="tertiary">
            {{ !isPublished ? $t('common.publish') : $t('common.unpublish') }}
          </UIButton>
        </template>
      </UITooltip>
    </div>
    <div v-if="!isContentLibraryTemplate">
      <UIButton :disabled="loading" ghost type="default" @click="useAsTemplateAction" id="delete-template-btn">
        {{ $t('common.useTemplate') }}
      </UIButton>
    </div>
    <div class="relative" v-if="builderStore.isDocumentEditable">
      <UIButton :loading="loading" :disabled="loading" ghost @click="onClickSaveAction" id="save-document-btn" type="primary">
        {{ $t('common.save') }}
      </UIButton>
      <span class="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1" v-if="appStore.unsavedChanges">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-warning-400 opacity-75"></span>
        <span class="relative inline-flex rounded-full h-3 w-3 bg-warning-500"></span>
      </span>
    </div>
  </div>
</template>

<style>
  .is-disabled {
    pointer-events: none;
    opacity: 0.5;
  }
</style>
